// src/pages/admin/AdminNodes.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Field, TextInput, PrimaryBtn, GhostBtn, Toast, AdminCard, AdminHeading, ConfirmDialog } from '../../components/AdminUI';
import RichTextEditor from '../../components/RichTextEditor';

function blankNode() {
  return { id: Date.now().toString(), name: '', description: '', imageUrl: '' };
}

function NodeEditor({ node, onChange, onDelete, index }) {
  const set = (k, v) => onChange({ ...node, [k]: v });
  return (
    <AdminCard style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', letterSpacing: 1 }}>
          NODE {String(index + 1).padStart(2, '0')}
        </span>
        <PrimaryBtn onClick={onDelete} danger small>Remove</PrimaryBtn>
      </div>

      <Field label="Node name" hint="Shown in the table of contents and as section heading">
        <TextInput
          value={node.name}
          onChange={v => set('name', v)}
          placeholder="e.g. Audio Loader Node"
        />
      </Field>

      <Field label="Description" hint="Supports bold, italic, headings, links, images and YouTube embeds.">
        <RichTextEditor
          value={node.description}
          onChange={v => set('description', v)}
        />
      </Field>

      <Field label="Preview image URL" hint="Optional — direct URL to a screenshot or demo image">
        <TextInput
          value={node.imageUrl || ''}
          onChange={v => set('imageUrl', v)}
          placeholder="https://…"
          mono
        />
      </Field>
    </AdminCard>
  );
}

export default function AdminNodes() {
  const [cfg, setCfg]         = useState({ repoUrl: '', repoName: '', authorHandle: '', description: '', images: [], nodes: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState({ msg: '', type: 'success' });
  const [confirmIdx, setConfirmIdx] = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  }

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'config', 'nodes'));
        if (snap.exists()) setCfg(prev => ({ ...prev, ...snap.data() }));
      } catch (e) {
        showToast('Failed to load config.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  function updateNode(idx, updated) {
    setCfg(prev => {
      const next = [...(prev.nodes || [])];
      next[idx] = updated;
      return { ...prev, nodes: next };
    });
  }
  function removeNode(idx) {
    setCfg(prev => ({
      ...prev,
      nodes: (prev.nodes || []).filter((_, i) => i !== idx),
    }));
    setConfirmIdx(null);
  }
  function addNode() {
    setCfg(prev => ({
      ...prev,
      nodes: [...(prev.nodes || []), blankNode()],
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'nodes'), { ...cfg, updatedAt: serverTimestamp() });
      showToast('Nodes config saved.');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ fontSize: 13, color: 'var(--sand-hint)' }}>Loading…</p>;

  return (
    <div>
      <AdminHeading>Nodes tab config</AdminHeading>
      <p style={{ fontSize: 12, color: 'var(--sand-hint)', fontStyle: 'italic', marginBottom: 20 }}>
        Controls the public Nodes tab — repo info, intro description, and the individual node list.
      </p>

      {/* Repo settings */}
      <AdminCard>
        <Field label="Repository URL" hint="Full GitHub URL">
          <TextInput value={cfg.repoUrl} onChange={v => set('repoUrl', v)} placeholder="https://github.com/…" mono />
        </Field>
        <Field label="Repository name">
          <TextInput value={cfg.repoName} onChange={v => set('repoName', v)} placeholder="comfyui-axces2000" mono />
        </Field>
        <Field label="Author handle">
          <TextInput value={cfg.authorHandle} onChange={v => set('authorHandle', v)} placeholder="axces2000" mono />
        </Field>
        <Field label="Intro description" hint="Shown under the repo name. Supports rich text.">
          <RichTextEditor value={cfg.description} onChange={v => set('description', v)} />
        </Field>
      </AdminCard>

      {/* Node list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)' }}>
          Individual nodes ({(cfg.nodes || []).length})
        </h3>
        <GhostBtn onClick={addNode}>+ Add node</GhostBtn>
      </div>

      {(cfg.nodes || []).length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--sand-hint)', fontStyle: 'italic', marginBottom: 16 }}>
          No nodes yet. Add one to build the table of contents.
        </p>
      ) : (
        (cfg.nodes || []).map((node, idx) => (
          <NodeEditor
            key={node.id}
            node={node}
            index={idx}
            onChange={updated => updateNode(idx, updated)}
            onDelete={() => setConfirmIdx(idx)}
          />
        ))
      )}

      <div style={{ marginTop: 20 }}>
        <PrimaryBtn onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save all changes'}
        </PrimaryBtn>
      </div>

      {confirmIdx !== null && (
        <ConfirmDialog
          message={`Remove node "${cfg.nodes[confirmIdx]?.name || 'this node'}"?`}
          onConfirm={() => removeNode(confirmIdx)}
          onCancel={() => setConfirmIdx(null)}
        />
      )}
      <Toast message={toast.msg} type={toast.type} />
    </div>
  );
}
